import { getRegExpPattern } from './regExpPattern'
import type { PasswordCharacterPatterns, PasswordRules } from './types'

export type { PasswordCharacterPatterns, PasswordRules }

export const DefaultPasswordRules: PasswordRules = {
  characterPatterns: [/\d|\W/],
  minimumLength: 8,
}

/**
 * Convert `patterns` into a single `RegExp` that enforces a match for all the
 * provided patterns.
 *
 * @example
 *   flattenCharacterPatterns([/\d/])
 *   //=> (?=.*\d).*
 * @example
 *   flattenCharacterPatterns([/\d|\W/, /[a-z]/, /[A-Z]/])
 *   //=> (?=.*(\d|\W))(?=.*[a-z])(?=.*[A-Z]).*
 *
 * @note If you use this with the HTML `pattern` attribute, the browser will
 *   implicitly wrap the `RegExp` this returns with `^(?:` and `)$`.
 * @see https://html.spec.whatwg.org/multipage/input.html#attr-input-pattern
 */
export function flattenCharacterPatterns(
  patterns: PasswordCharacterPatterns
): RegExp {
  // `(?=pattern)` is positive lookahead -- it asserts that the given pattern
  // must be present in the string. Lookaheads can be declared in any order,
  // *but* if they're intended to match at any point in the string they must
  // include the `.*` wildcard to allow preceding characters. The lookaheads
  // must be followed by `.*` to actually perform the matches.
  //
  // For example, to capture an entire string that matches all of `/d|/W`,
  // `/[a-z]/`, and `/[A-Z]/`:
  // `(?=.*(\d|\W))(?=.*[a-z])(?=.*[A-Z]).*`
  //
  // @see https://stackoverflow.com/a/54417192
  // @see https://www.regular-expressions.info/lookaround.html
  const lookaheads = patterns.map((pattern) => {
    const asString = pattern.source
    // If `pattern` has alternatives, ensure it's wrapped in parentheses.
    // Otherwise, if `pattern` is `\d|\W`, the output would be `(?=.*\d|\W)`,
    // which matches `.*\d` or `\W`; the intention is to match `.*\d` or
    // `.*\W`.
    const corrected =
      asString.includes('|') && !/^\(.+|.+\)$/.test(asString)
        ? `(${asString})`
        : asString
    return `(?=.*${corrected})`
  })

  return new RegExp([...lookaheads, '.*'].join(''))
}

/**
 * Convert `patterns` into a textual description of the provided patterns.
 *
 * @example
 *   humanizeCharacterPatterns([/\d/])
 *   //=> "a digit"
 *   humanizeCharacterPatterns([/\W/])
 *   //=> "a symbol"
 *   humanizeCharacterPatterns([/\w/])
 *   //=> ""
 *   humanizeCharacterPatterns([/\d/, /\W/, /[a-z]/])
 *   //=> "a digit, a symbol, and a lowercase letter"
 *   humanizeCharacterPatterns([/[a-z]/, /\d|\W/])
 *   //=> "a lowercase letter and a digit or a symbol"
 */
export function humanizeCharacterPatterns(
  patterns: PasswordCharacterPatterns
): string {
  const mapped = patterns.reduce((memo: string[], pattern): string[] => {
    const mapping = getRegExpPattern(pattern).map((part) => part.description)
    if (mapping.length > 0) memo.push(mapping.join(' or '))
    return memo
  }, [])

  if (mapped.length <= 2) return mapped.join(' and ')

  const last = mapped.slice(-1)[0]
  const withoutLast = mapped.slice(0, -1)
  return `${withoutLast.join(', ')}, and ${last}`
}

/**
 * Convert `patterns` to a `passwordrules` attribute value.
 *
 * @example
 *   passwordrulesAttribute([/\d/, /\W/, /[a-z]/])
 *   //=> "allowed: unicode; required: digit; required: special; required: lower;"
 *
 * @see https://developer.apple.com/documentation/security/password_autofill/customizing_password_autofill_rules
 * @see https://developer.apple.com/password-rules/
 */
export function passwordrulesAttribute(
  patterns: PasswordCharacterPatterns
): string {
  const mapped = patterns.reduce((memo: string[], pattern) => {
    // @note `passwordrules` can't represent the concept of 'must have a digit
    //   or symbol'--you can allow one of a set of keywords, or you can require
    //   something from a specific list of characters, or you can require
    //   multiple keywords. Ultimately this limitation doesn't really affect
    //   user experience, though, because the machine takes care of generating
    //   a password that matches all the requirements.
    //
    // @see https://developer.apple.com/documentation/security/password_autofill/customizing_password_autofill_rules
    const mapping = getRegExpPattern(pattern).map(
      (part) => `required: ${part.name};`
    )
    return memo.concat(mapping)
  }, [])

  return `allowed: unicode; ${mapped.join(' ')}`
}
